import React from "react"

import classNames from "classnames"

import "./styles/_media-option.scss"

export interface MediaOptionRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "inline" | "stacked" | "product"
  children: React.ReactNode
}

export interface MediaOptionMediaProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface MediaOptionLabelProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
}

export interface MediaOptionSubtextProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
}

const Root = React.forwardRef<HTMLDivElement, MediaOptionRootProps>(
  ({ className, children, variant = "inline", ...props }, ref) => {
    return (
      <div
        {...props}
        ref={ref}
        className={classNames(
          "media-option",
          `media-option--${variant}`,
          className
        )}
      >
        {children}
      </div>
    )
  }
)
Root.displayName = "MediaOptionRoot"

const Media = React.forwardRef<HTMLDivElement, MediaOptionMediaProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        {...props}
        ref={ref}
        className={classNames("media-option__media", className)}
      >
        {children}
      </div>
    )
  }
)
Media.displayName = "MediaOptionMedia"

const Label = React.forwardRef<HTMLSpanElement, MediaOptionLabelProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        {...props}
        ref={ref}
        className={classNames("media-option__label", className)}
      >
        {children}
      </span>
    )
  }
)
Label.displayName = "MediaOptionLabel"

const Subtext = React.forwardRef<HTMLSpanElement, MediaOptionSubtextProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        {...props}
        ref={ref}
        className={classNames("media-option__subtext", className)}
      >
        {children}
      </span>
    )
  }
)
Subtext.displayName = "MediaOptionSubtext"

/**
 * MediaOption compound component for displaying an icon/image with label and optional subtext
 *
 * @example
 * ```tsx
 * <MediaOption.Root>
 *   <MediaOption.Media>
 *     <Icon name="building-type-hotel" />
 *   </MediaOption.Media>
 *   <MediaOption.Label>Hotel</MediaOption.Label>
 *   <MediaOption.Subtext>Optional description</MediaOption.Subtext>
 * </MediaOption.Root>
 * ```
 */
export const MediaOption = {
  Root,
  Media,
  Label,
  Subtext
}
